import React from 'react';
import { Theme } from '../Theme';

type ClinicalNotesEditorProps = {
    value: string;
    onChange: (value: string) => void;
    onBlur?: (value: string) => void;
    markerTimestampMs: number;
    onMarkerClick?: (timestampMs: number) => void;
    disabled?: boolean;
    placeholder?: string;
    showMarkerControls?: boolean;
};

type ClinicalNotesPart =
    | { type: 'text'; text: string }
    | { type: 'marker'; timestampMs: number; label: string };

const CLINICAL_NOTE_MARKER_REGEX = /\[\[marker:(\d+)\|([^\]]+)\]\]/g;

export function formatClinicalNoteMarkerTime(timestampMs: number): string {
    // Keep note markers aligned with the audio player's compact time format.
    const totalSeconds = Math.max(0, Math.floor(timestampMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function createClinicalNoteMarkerToken(timestampMs: number): string {
    return `[[marker:${Math.max(0, Math.round(timestampMs))}|${formatClinicalNoteMarkerTime(timestampMs)}]]`;
}

export function formatClinicalNotesForPlainText(notes: string): string {
    // Make saved marker tokens readable wherever notes are used as plain text.
    return notes.replace(CLINICAL_NOTE_MARKER_REGEX, (_match, _timestampMs, label) => `[${label}]`);
}

function parseClinicalNotes(value: string): ClinicalNotesPart[] {
    const parts: ClinicalNotesPart[] = [];
    let cursor = 0;
    let match = CLINICAL_NOTE_MARKER_REGEX.exec(value);

    while (match) {
        if (match.index > cursor) {
            parts.push({ type: 'text', text: value.slice(cursor, match.index) });
        }

        parts.push({
            type: 'marker',
            timestampMs: Number(match[1]),
            label: match[2] || formatClinicalNoteMarkerTime(Number(match[1])),
        });
        cursor = match.index + match[0].length;
        match = CLINICAL_NOTE_MARKER_REGEX.exec(value);
    }

    if (cursor < value.length) {
        parts.push({ type: 'text', text: value.slice(cursor) });
    }

    return parts.length > 0 ? parts : [{ type: 'text', text: '' }];
}

function serializeClinicalNotesNode(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || '';
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
    }

    const element = node as HTMLElement;
    if (element.dataset.clinicalNoteMarker === 'true') {
        const timestampMs = Number(element.dataset.markerTimestampMs || 0);
        return createClinicalNoteMarkerToken(timestampMs);
    }

    if (element.tagName === 'BR') {
        return '\n';
    }

    const childrenText = Array.from(element.childNodes).map(serializeClinicalNotesNode).join('');
    if (element.tagName === 'DIV' || element.tagName === 'P') {
        return `${childrenText}\n`;
    }

    return childrenText;
}

function serializeClinicalNotesEditor(editorElement: HTMLElement | null): string {
    if (!editorElement) {
        return '';
    }

    return Array.from(editorElement.childNodes)
        .map(serializeClinicalNotesNode)
        .join('')
        .replace(/\n$/g, '');
}

function isSelectionInsideElement(element: HTMLElement, selection: Selection | null): boolean {
    if (!selection || selection.rangeCount === 0) {
        return false;
    }

    const range = selection.getRangeAt(0);
    return element.contains(range.commonAncestorContainer);
}

function isRangeInsideElement(element: HTMLElement, range: Range | null): boolean {
    return !!range && element.contains(range.commonAncestorContainer);
}

export function ClinicalNotesEditor(props: ClinicalNotesEditorProps) {
    const {
        value,
        onChange,
        onBlur,
        markerTimestampMs,
        onMarkerClick,
        disabled = false,
        placeholder = 'Add clinical notes for this session.',
        showMarkerControls = true,
    } = props;
    const editorRef = React.useRef<HTMLDivElement | null>(null);
    const markerClickRef = React.useRef(onMarkerClick);
    const lastSelectionRangeRef = React.useRef<Range | null>(null);
    const didInsertMarkerFromPointerRef = React.useRef(false);
    const [isFocused, setIsFocused] = React.useState(false);

    React.useEffect(() => {
        markerClickRef.current = onMarkerClick;
    }, [onMarkerClick]);

    const createMarkerButtonElement = React.useCallback((timestampMs: number, label: string) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        button.contentEditable = 'false';
        button.dataset.clinicalNoteMarker = 'true';
        button.dataset.markerTimestampMs = String(Math.max(0, Math.round(timestampMs)));
        Object.assign(button.style, markerButtonDomStyles);
        button.onmousedown = (event) => {
            event.preventDefault();
        };
        button.onclick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            markerClickRef.current?.(timestampMs);
        };
        return button;
    }, []);

    const renderValueIntoEditor = React.useCallback((nextValue: string) => {
        const editorElement = editorRef.current;
        if (!editorElement) {
            return;
        }

        editorElement.replaceChildren();
        parseClinicalNotes(nextValue).forEach((part) => {
            if (part.type === 'marker') {
                editorElement.appendChild(createMarkerButtonElement(part.timestampMs, part.label));
                return;
            }

            editorElement.appendChild(document.createTextNode(part.text));
        });
    }, [createMarkerButtonElement]);

    React.useLayoutEffect(() => {
        const editorElement = editorRef.current;
        if (!editorElement) {
            return;
        }

        if (serializeClinicalNotesEditor(editorElement) !== value) {
            renderValueIntoEditor(value);
        }
    }, [renderValueIntoEditor, value]);

    const rememberSelection = React.useCallback(() => {
        const editorElement = editorRef.current;
        const selection = window.getSelection();
        if (!editorElement || !isSelectionInsideElement(editorElement, selection)) {
            return;
        }

        lastSelectionRangeRef.current = selection!.getRangeAt(0).cloneRange();
    }, []);

    React.useEffect(() => {
        // Track caret moves that do not fire input events, such as mouse clicks and arrow keys.
        document.addEventListener('selectionchange', rememberSelection);
        return () => document.removeEventListener('selectionchange', rememberSelection);
    }, [rememberSelection]);

    const emitChange = React.useCallback(() => {
        rememberSelection();
        onChange(serializeClinicalNotesEditor(editorRef.current));
    }, [onChange, rememberSelection]);

    const handleAddMarker = React.useCallback(() => {
        // Insert a timestamp marker at the current caret, or append if focus is elsewhere.
        const editorElement = editorRef.current;
        if (!editorElement || disabled) {
            return;
        }

        const selection = window.getSelection();
        let range: Range;
        if (isSelectionInsideElement(editorElement, selection)) {
            range = selection!.getRangeAt(0).cloneRange();
        } else if (isRangeInsideElement(editorElement, lastSelectionRangeRef.current)) {
            range = lastSelectionRangeRef.current!.cloneRange();
        } else {
            range = document.createRange();
            range.selectNodeContents(editorElement);
            range.collapse(false);
        }

        const markerButton = createMarkerButtonElement(
            markerTimestampMs,
            formatClinicalNoteMarkerTime(markerTimestampMs)
        );
        const trailingSpace = document.createTextNode(' ');
        const markerFragment = document.createDocumentFragment();
        markerFragment.appendChild(markerButton);
        markerFragment.appendChild(trailingSpace);
        range.deleteContents();
        range.insertNode(markerFragment);
        range.setStartAfter(trailingSpace);
        range.setEndAfter(trailingSpace);
        editorElement.focus();
        selection?.removeAllRanges();
        selection?.addRange(range);
        lastSelectionRangeRef.current = range.cloneRange();
        emitChange();
    }, [createMarkerButtonElement, disabled, emitChange, markerTimestampMs]);

    const handleAddMarkerPointerDown = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        // Insert before the button click steals the contenteditable selection.
        event.preventDefault();
        didInsertMarkerFromPointerRef.current = true;
        rememberSelection();
        handleAddMarker();
    }, [handleAddMarker, rememberSelection]);

    const handleAddMarkerClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if (didInsertMarkerFromPointerRef.current) {
            didInsertMarkerFromPointerRef.current = false;
            return;
        }

        handleAddMarker();
    }, [handleAddMarker]);

    const showPlaceholder = !isFocused && !value;

    return (
        <div style={styles.editorShell}>
            {showMarkerControls && (
                <div style={styles.toolbar}>
                    <button
                        type='button'
                        onMouseDown={handleAddMarkerPointerDown}
                        onClick={handleAddMarkerClick}
                        disabled={disabled}
                        style={{
                            ...styles.addMarkerButton,
                            ...(disabled ? styles.addMarkerButtonDisabled : {}),
                        }}
                    >
                        Add marker
                    </button>
                    <span style={styles.markerTimePreview}>
                        {formatClinicalNoteMarkerTime(markerTimestampMs)}
                    </span>
                </div>
            )}
            <div style={styles.editorFrame}>
                {showPlaceholder && (
                    <div style={styles.placeholder}>
                        {placeholder}
                    </div>
                )}
                <div
                    ref={editorRef}
                    contentEditable={!disabled}
                    suppressContentEditableWarning
                    onInput={emitChange}
                    onBlur={() => {
                        setIsFocused(false);
                        rememberSelection();
                        const nextValue = serializeClinicalNotesEditor(editorRef.current);
                        onChange(nextValue);
                        onBlur?.(nextValue);
                    }}
                    onFocus={() => {
                        setIsFocused(true);
                        rememberSelection();
                    }}
                    onKeyUp={rememberSelection}
                    onMouseUp={rememberSelection}
                    style={{
                        ...styles.editor,
                        ...(disabled ? styles.editorDisabled : {}),
                    }}
                    role='textbox'
                    aria-multiline='true'
                    aria-label='Clinical notes'
                />
            </div>
        </div>
    );
}

const markerButtonDomStyles: Partial<CSSStyleDeclaration> = {
    appearance: 'none',
    webkitAppearance: 'none',
    border: '1px solid rgba(37, 99, 235, 0.32)',
    borderRadius: '999px',
    backgroundColor: 'rgba(37, 99, 235, 0.1)',
    color: '#1d4ed8',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    font: 'inherit',
    fontWeight: '700',
    margin: '0 3px',
    padding: '1px 7px',
    verticalAlign: 'baseline',
};

const styles: Record<string, React.CSSProperties> = {
    editorShell: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    addMarkerButton: {
        ...Theme.Fonts.body,
        appearance: 'none',
        WebkitAppearance: 'none',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.workArea,
        color: Theme.Colours.text,
        cursor: 'pointer',
        fontWeight: 700,
        padding: '6px 10px',
    },
    addMarkerButtonDisabled: {
        cursor: 'not-allowed',
        opacity: 0.55,
    },
    markerTimePreview: {
        ...Theme.Fonts.body,
        color: 'rgba(0, 0, 0, 0.55)',
        fontSize: '13px',
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 700,
    },
    editorFrame: {
        position: 'relative',
        width: '100%',
    },
    placeholder: {
        ...Theme.Fonts.body,
        position: 'absolute',
        top: '10px',
        left: '12px',
        color: 'rgba(0, 0, 0, 0.42)',
        pointerEvents: 'none',
        lineHeight: 1.5,
    },
    editor: {
        ...Theme.Fonts.body,
        width: '100%',
        minHeight: '92px',
        boxSizing: 'border-box',
        overflowY: 'auto',
        padding: '10px 12px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.workArea,
        color: Theme.Colours.text,
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        outline: 'none',
    },
    editorDisabled: {
        opacity: 0.68,
        cursor: 'not-allowed',
    },
};
